import { lazy, Suspense, useMemo, useState } from "react";
import {
  Building2,
  Calendar,
  CalendarDays,
  ChevronRight,
  LayoutGrid,
  List,
  Loader2,
  Lock,
  Search,
  Sparkles,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";

const CyclesCalendar = lazy(() => import("@/components/CyclesCalendar"));

type Retailer = { id: number; name: string; calendarColor: string };

type Cycle = {
  id: number;
  retailerId: number;
  code: string;
  year: number;
  /** 1–12, cycle “anchor” month for timeline strip */
  startMonth: number;
  /** Inclusive end month for multi-month cycles (e.g. quarters); defaults to startMonth. */
  endMonth?: number;
  locked: boolean;
  totalCampaigns: number;
  confirmedCampaigns: number;
};

const RETAILERS: Retailer[] = [
  { id: 3, name: "Dunnes Stores", calendarColor: "#2563eb" },
  { id: 1, name: "SuperValu", calendarColor: "#16a34a" },
  { id: 2, name: "Centra", calendarColor: "#ca8a04" },
  { id: 4, name: "Lidl Ireland", calendarColor: "#dc2626" },
  { id: 5, name: "Aldi Ireland", calendarColor: "#7c3aed" },
];

/** Static sample cycles — no API, no pagination. */
const SAMPLE_CYCLES: Cycle[] = [
  { id: 40, retailerId: 3, code: "1, 2026", year: 2026, startMonth: 1, locked: true, totalCampaigns: 48, confirmedCampaigns: 40 },
  { id: 41, retailerId: 3, code: "2, 2026", year: 2026, startMonth: 2, locked: true, totalCampaigns: 32, confirmedCampaigns: 28 },
  { id: 42, retailerId: 3, code: "3, 2026", year: 2026, startMonth: 3, locked: false, totalCampaigns: 24, confirmedCampaigns: 10 },
  { id: 43, retailerId: 3, code: "4, 2026", year: 2026, startMonth: 4, locked: false, totalCampaigns: 18, confirmedCampaigns: 6 },
  { id: 44, retailerId: 3, code: "5, 2026", year: 2026, startMonth: 5, locked: false, totalCampaigns: 0, confirmedCampaigns: 0 },
  { id: 45, retailerId: 3, code: "6, 2026", year: 2026, startMonth: 6, locked: false, totalCampaigns: 12, confirmedCampaigns: 0 },
  { id: 46, retailerId: 3, code: "7, 2026", year: 2026, startMonth: 7, locked: false, totalCampaigns: 8, confirmedCampaigns: 2 },
  { id: 101, retailerId: 1, code: "Q1 2026", year: 2026, startMonth: 1, endMonth: 3, locked: true, totalCampaigns: 22, confirmedCampaigns: 20 },
  { id: 102, retailerId: 1, code: "Q2 2026", year: 2026, startMonth: 4, endMonth: 6, locked: false, totalCampaigns: 15, confirmedCampaigns: 4 },
  { id: 103, retailerId: 2, code: "Spring 2026", year: 2026, startMonth: 3, endMonth: 5, locked: false, totalCampaigns: 9, confirmedCampaigns: 9 },
  { id: 104, retailerId: 4, code: "Cycle A", year: 2026, startMonth: 2, locked: true, totalCampaigns: 60, confirmedCampaigns: 55 },
  { id: 201, retailerId: 3, code: "11, 2025", year: 2025, startMonth: 11, locked: true, totalCampaigns: 36, confirmedCampaigns: 36 },
  { id: 202, retailerId: 3, code: "12, 2025", year: 2025, startMonth: 12, locked: true, totalCampaigns: 28, confirmedCampaigns: 22 },
];

const MONTHS = [
  { m: 1, label: "Jan" },
  { m: 2, label: "Feb" },
  { m: 3, label: "Mar" },
  { m: 4, label: "Apr" },
  { m: 5, label: "May" },
  { m: 6, label: "Jun" },
  { m: 7, label: "Jul" },
  { m: 8, label: "Aug" },
  { m: 9, label: "Sep" },
  { m: 10, label: "Oct" },
  { m: 11, label: "Nov" },
  { m: 12, label: "Dec" },
];

type StatusFilter = "all" | "active" | "draft" | "locked";

function retailerStats(retailerId: number, year: number) {
  const list = SAMPLE_CYCLES.filter((c) => c.retailerId === retailerId && c.year === year);
  const locked = list.filter((c) => c.locked).length;
  const campaigns = list.reduce((s, c) => s + c.totalCampaigns, 0);
  return { count: list.length, locked, campaigns };
}

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

/** Inclusive calendar date range for `CyclesCalendar` (YYYY-MM-DD). */
function monthRangeISO(year: number, startMonth: number, endMonth?: number) {
  const endM = endMonth ?? startMonth;
  const startDate = `${year}-${pad2(startMonth)}-01`;
  const lastDay = new Date(year, endM, 0).getDate();
  const endDate = `${year}-${pad2(endM)}-${pad2(lastDay)}`;
  return { startDate, endDate };
}

function cycleSpansMonth(c: Cycle, month1to12: number) {
  const end = c.endMonth ?? c.startMonth;
  return month1to12 >= c.startMonth && month1to12 <= end;
}

export default function CyclesPrototype() {
  const [year, setYear] = useState(2026);
  const [selectedRetailerId, setSelectedRetailerId] = useState(3);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [monthJump, setMonthJump] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState<"list" | "calendar">("list");

  const railItems = useMemo(() => {
    return RETAILERS.map((r) => {
      const { count, locked, campaigns } = retailerStats(r.id, year);
      return { retailer: r, count, locked, campaigns };
    });
  }, [year]);

  const filteredCycles = useMemo(() => {
    let list = SAMPLE_CYCLES.filter((c) => c.retailerId === selectedRetailerId && c.year === year);

    if (statusFilter === "locked") list = list.filter((c) => c.locked);
    if (statusFilter === "active") list = list.filter((c) => c.totalCampaigns > 0 && !c.locked);
    if (statusFilter === "draft") list = list.filter((c) => c.totalCampaigns === 0 && !c.locked);

    if (monthJump != null) list = list.filter((c) => cycleSpansMonth(c, monthJump));

    const q = search.trim().toLowerCase();
    if (q) list = list.filter((c) => c.code.toLowerCase().includes(q) || String(c.id).includes(q));

    return list.sort((a, b) => a.startMonth - b.startMonth || a.id - b.id);
  }, [selectedRetailerId, year, statusFilter, monthJump, search]);

  const cyclesForCalendar = useMemo(() => {
    return filteredCycles.map((c) => {
      const { startDate, endDate } = monthRangeISO(c.year, c.startMonth, c.endMonth);
      const otherCampaigns = Math.max(0, c.totalCampaigns - c.confirmedCampaigns);
      return {
        id: c.id,
        code: c.code,
        name: `Cycle ${c.code}`,
        retailerId: c.retailerId,
        startDate,
        endDate,
        locked_flag: c.locked,
        totalCampaigns: c.totalCampaigns,
        confirmedCampaigns: c.confirmedCampaigns,
        otherCampaigns,
      };
    });
  }, [filteredCycles]);

  const selectedRetailer = RETAILERS.find((r) => r.id === selectedRetailerId) ?? RETAILERS[0];
  const stats = retailerStats(selectedRetailerId, year);

  return (
    <div className="min-h-screen bg-slate-100 p-4 md:p-6">
      <div className="mx-auto max-w-[1600px] space-y-5">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm text-slate-500">
              Prototype <ChevronRight className="h-3 w-3" /> Cycles dashboard
            </div>
            <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-950 md:text-3xl">
              <LayoutGrid className="h-7 w-7 text-primary" />
              Cycles overview
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-slate-600">
              Retailer-first: pick a retailer from the rail, then see every matching cycle in a grid — no paging through unrelated tiles and no horizontal strip scrolling.
            </p>
          </div>
          <Badge variant="secondary" className="shrink-0 gap-1">
            <Sparkles className="h-3 w-3" />
            Mock data only
          </Badge>
        </header>

        <div className="grid gap-5 lg:grid-cols-[minmax(0,260px)_minmax(0,1fr)]">
          {/* Retailer rail */}
          <Card className="h-fit lg:sticky lg:top-4">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Retailers</CardTitle>
              <CardDescription>One tap — all cycles for that retailer load in the grid.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {railItems.map(({ retailer, count, locked, campaigns }) => (
                <button
                  key={retailer.id}
                  type="button"
                  onClick={() => {
                    setSelectedRetailerId(retailer.id);
                    setMonthJump(null);
                  }}
                  className={cn(
                    "flex w-full flex-col gap-1 rounded-lg border bg-white p-3 text-left text-sm transition hover:border-primary/50 hover:shadow-sm",
                    selectedRetailerId === retailer.id && "border-primary ring-2 ring-primary/15",
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2 font-semibold text-slate-950">
                      <Building2 className="h-4 w-4 text-slate-500" />
                      {retailer.name}
                    </span>
                    {count > 0 ? (
                      <Badge variant="outline" className="shrink-0">
                        {count}
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="shrink-0 text-xs">
                        none
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-500">
                    <span>{campaigns} campaigns</span>
                    <span>{locked} locked</span>
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>

          <div className="space-y-4">
            {/* Year + status + search */}
            <Card>
              <CardContent className="flex flex-col gap-4 pt-6">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm font-medium text-slate-700">Year</span>
                  <div className="flex gap-2">
                    {[2025, 2026].map((y) => (
                      <Button
                        key={y}
                        type="button"
                        size="sm"
                        variant={year === y ? "default" : "outline"}
                        onClick={() => {
                          setYear(y);
                          setMonthJump(null);
                        }}
                      >
                        {y}
                      </Button>
                    ))}
                  </div>
                  <div className="hidden h-6 w-px bg-border sm:block" />
                  <span className="text-sm font-medium text-slate-700">Status</span>
                  <div className="flex flex-wrap gap-2">
                    {(
                      [
                        { id: "all" as const, label: "All" },
                        { id: "active" as const, label: "Active" },
                        { id: "draft" as const, label: "Draft" },
                        { id: "locked" as const, label: "Locked" },
                      ] as const
                    ).map((chip) => (
                      <Button
                        key={chip.id}
                        type="button"
                        size="sm"
                        variant={statusFilter === chip.id ? "default" : "outline"}
                        onClick={() => setStatusFilter(chip.id)}
                      >
                        {chip.label}
                      </Button>
                    ))}
                  </div>
                  <div className="ml-auto w-full min-w-[200px] max-w-xs sm:w-64">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search cycle code or id…"
                        className="pl-8"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Month strip — jump within year */}
                <div>
                  <div className="mb-2 flex items-center gap-2 text-xs font-medium text-slate-600">
                    <CalendarDays className="h-3.5 w-3.5" />
                    Jump by month (optional)
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <Button
                      type="button"
                      size="sm"
                      variant={monthJump === null ? "secondary" : "ghost"}
                      className="h-8 text-xs"
                      onClick={() => setMonthJump(null)}
                    >
                      All months
                    </Button>
                    {MONTHS.map(({ m, label }) => {
                      const has = SAMPLE_CYCLES.some(
                        (c) =>
                          c.retailerId === selectedRetailerId &&
                          c.year === year &&
                          cycleSpansMonth(c, m),
                      );
                      return (
                        <Button
                          key={m}
                          type="button"
                          size="sm"
                          variant={monthJump === m ? "default" : "outline"}
                          className={cn("h-8 min-w-[2.5rem] px-2 text-xs", !has && "opacity-40")}
                          disabled={!has}
                          onClick={() => setMonthJump(m)}
                        >
                          {label}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* View mode — same Tile / Calendar toggle as live Cycles dashboard */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-700">View:</span>
                    <ToggleGroup
                      type="single"
                      value={viewMode}
                      onValueChange={(value) => {
                        if (value === "list" || value === "calendar") {
                          setViewMode(value);
                        }
                      }}
                    >
                      <ToggleGroupItem value="list" aria-label="Tile view">
                        <List className="mr-2 h-4 w-4" />
                        Tile View
                      </ToggleGroupItem>
                      <ToggleGroupItem value="calendar" aria-label="Calendar view">
                        <Calendar className="mr-2 h-4 w-4" />
                        Calendar View
                      </ToggleGroupItem>
                    </ToggleGroup>
                  </div>
                  {viewMode === "calendar" && (
                    <p className="text-xs text-muted-foreground">
                      Same calendar component as the live dashboard; mock cycle clicks do not open details.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Selected retailer summary */}
            <div className="flex flex-wrap items-baseline justify-between gap-2 px-1">
              <h2 className="text-lg font-semibold text-slate-900">
                {selectedRetailer.name}{" "}
                <span className="font-normal text-slate-500">
                  · {year} · {filteredCycles.length} cycle{filteredCycles.length === 1 ? "" : "s"} shown
                </span>
              </h2>
              <p className="text-sm text-slate-500">
                {stats.count} in year · {stats.locked} locked · {stats.campaigns} campaigns total
              </p>
            </div>

            {/* Tile grid or calendar — same calendar component as /cycles */}
            {filteredCycles.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
                  <Calendar className="h-12 w-12 text-slate-300" />
                  <p className="text-lg font-medium text-slate-600">No cycles match</p>
                  <p className="max-w-md text-sm text-slate-500">
                    Try another year, clear the month filter, or switch status. In the live app, empty lists often happen when pagination returns a page that does not contain your retailer — this prototype loads all cycles for the selected retailer instead.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setStatusFilter("all");
                      setMonthJump(null);
                      setSearch("");
                    }}
                  >
                    Reset filters
                  </Button>
                </CardContent>
              </Card>
            ) : viewMode === "calendar" ? (
              <Suspense
                fallback={
                  <Card>
                    <CardContent className="flex items-center justify-center gap-2 py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                      <span className="text-muted-foreground">Loading calendar…</span>
                    </CardContent>
                  </Card>
                }
              >
                <CyclesCalendar
                  cycles={cyclesForCalendar}
                  retailers={RETAILERS}
                  showAllEvents={false}
                  readOnly
                />
              </Suspense>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {filteredCycles.map((cycle) => (
                  <Card
                    key={cycle.id}
                    className={cn(
                      "border-2 transition-shadow hover:shadow-md",
                      cycle.locked ? "border-red-200 bg-red-50/50" : "border-slate-200 bg-white",
                    )}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-slate-500" />
                        <span className="font-semibold text-slate-900">Cycle {cycle.code}</span>
                        {cycle.locked && <Lock className="h-4 w-4 text-red-600" aria-hidden />}
                      </div>
                      <p className="mt-1 text-xs text-slate-500">
                        {cycle.endMonth != null && cycle.endMonth !== cycle.startMonth
                          ? `${MONTHS.find((x) => x.m === cycle.startMonth)?.label}–${MONTHS.find((x) => x.m === cycle.endMonth)?.label}`
                          : MONTHS.find((x) => x.m === cycle.startMonth)?.label}{" "}
                        {cycle.year} · id {cycle.id}
                      </p>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-3 gap-2 text-center text-sm">
                        <div className="rounded-md bg-slate-50 py-2">
                          <div className="font-bold text-slate-900">{cycle.totalCampaigns}</div>
                          <div className="text-[10px] text-slate-500">Total</div>
                        </div>
                        <div className="rounded-md bg-emerald-50 py-2">
                          <div className="font-bold text-emerald-700">{cycle.confirmedCampaigns}</div>
                          <div className="text-[10px] text-slate-500">Confirmed</div>
                        </div>
                        <div className="rounded-md bg-amber-50 py-2">
                          <div className="font-bold text-amber-800">{Math.max(0, cycle.totalCampaigns - cycle.confirmedCampaigns)}</div>
                          <div className="text-[10px] text-slate-500">Other</div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" size="sm" className="flex-1" disabled>
                          View (mock)
                        </Button>
                        <Button type="button" variant="outline" size="sm" className="flex-1" disabled>
                          Lock (mock)
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            <Card className="border-dashed bg-slate-50/80">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">How this differs from the current /cycles page</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-slate-600 space-y-2">
                <ul className="list-disc space-y-1 pl-5">
                  <li>
                    <strong>Retailer rail</strong> replaces a dropdown so context is always visible.
                  </li>
                  <li>
                    <strong>No server pagination</strong> for the selected slice — every matching cycle appears in a responsive grid (live app can fetch all pages for retailer+year like calendar mode already does).
                  </li>
                  <li>
                    <strong>Month strip</strong> gives a quick jump without losing the grid layout.
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
